(() => {
  if (customElements.get('and-inline-product-switch')) return;
  class InlineProductSwitch extends HTMLElement {
    static choose(config, product, value, selections) {
      const norm = value => String(value || '').trim().toLowerCase();
      const candidates = config.routes.filter(route => route.product === product && norm(route.value) === norm(value));
      const scored = candidates.map(route => ({route,score:route.names.reduce((score,name,index) => score + (norm(name) !== norm(config.optionName) && norm(selections[norm(name)]) && norm(selections[norm(name)]) === norm(route.options[index]) ? 1 : 0),0)}));
      scored.sort((a,b) => b.score-a.score || Number(b.route.available)-Number(a.route.available));
      return scored[0]?.route || null;
    }
    connectedCallback() {
      if (this.onClick) return;
      try { this.config = JSON.parse(this.querySelector('[data-product-option-routes]').textContent); } catch { return; }
      this.onClick = event => {
        const link = event.target.closest('a[data-product-handle][data-option-value]');
        if (!link || !this.contains(link)) return;
        const info = this.closest('product-info');
        const selections = {};
        this.config.currentOptionNames.forEach((name,index) => {
          const field = info?.querySelector('[data-option-index="option' + (index+1) + '"]');
          selections[name.trim().toLowerCase()] = field?.querySelector('input:checked')?.value || field?.querySelector('select')?.value || '';
        });
        const target = InlineProductSwitch.choose(this.config,link.dataset.productHandle,link.dataset.optionValue,selections);
        if(target?.url) link.setAttribute('href',target.url);
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        if (link.dataset.productHandle !== this.dataset.currentProduct) {
          event.preventDefault();
          this.switchProduct(link.href);
          return;
        }
        const index = this.config.currentOptionNames.findIndex(name => name.trim().toLowerCase() === this.config.optionName.trim().toLowerCase());
        const field = info?.querySelector('variant-selects [data-option-index="option' + (index+1) + '"]');
        const input = Array.from(field?.querySelectorAll('input[type="radio"]') || []).find(input => input.value === link.dataset.optionValue);
        const select = field?.matches('select') ? field : field?.querySelector('select');
        const option = Array.from(select?.options || []).find(option => option.value === link.dataset.optionValue);
        if (!input && !option) return;
        event.preventDefault();
        if (input) {
          if (input.checked) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', {bubbles:true}));
        } else {
          Array.from(select.options).forEach(item => item.toggleAttribute('selected',item === option));
          select.value = option.value;
          select.dispatchEvent(new Event('change', {bubbles:true}));
        }
        this.sync(link.dataset.optionValue);
      };
      this.addEventListener('click',this.onClick,true);
      const info = this.closest('product-info');
      if (window.theme?.pubsub) this.unsubscribe = window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.variantChange, ({data}) => {
        if (String(data.productId) !== info?.getAttribute('data-product-id') || String(data.sectionId) !== info?.getAttribute('data-section-id')) return;
        const index = this.config.currentOptionNames.findIndex(name => name.trim().toLowerCase() === this.config.optionName.trim().toLowerCase());
        if (data.variant?.options?.[index]) this.sync(data.variant.options[index]);
      });
    }
    async switchProduct(url) {
      if (this.busy) return;
      this.busy = true;
      this.setAttribute('aria-busy','true');
      const error = this.querySelector('[data-inline-error]');
      error.hidden = true;
      const main = document.getElementById('MainContent');
      const oldInfo = this.closest('product-info');
      oldInfo?.abortController?.abort();
      try {
        const targetUrl = new URL(url,location.href);
        if (targetUrl.origin !== location.origin) throw new Error('origin');
        const currentUrl = new URL(location.href);
        const previewTheme = currentUrl.searchParams.get('preview_theme_id') || (window.Shopify?.theme?.role !== 'main' ? window.Shopify?.theme?.id : null);
        if (previewTheme) targetUrl.searchParams.set('preview_theme_id',String(previewTheme));
        const previewKey = currentUrl.searchParams.get('preview_key');
        if (previewKey) targetUrl.searchParams.set('preview_key',previewKey);
        const response = await fetch(targetUrl.href,{credentials:'same-origin'});
        if (!response.ok) throw new Error('response');
        const parsed = new DOMParser().parseFromString(await response.text(),'text/html');
        const incoming = parsed.getElementById('MainContent');
        const nextControl = incoming?.querySelector('and-inline-product-switch');
        const nextInfo = nextControl?.closest('product-info');
        const targetHandle = targetUrl.pathname.split('/products/')[1]?.split('/')[0];
        if (nextControl?.dataset.currentProduct !== targetHandle) throw new Error('product');
        if (!nextInfo || !nextInfo.querySelector('form input[name="id"]') || !main) throw new Error('template');
        const loadedScripts = new Set(Array.from(document.scripts).map(node => node.src).filter(Boolean));
        const isReviewSection = node => node.nodeType === 1 && (node.matches('[data-and-review-section]') || node.querySelector('[data-and-review-section]'));
        const reviewSections = Array.from(main.children).filter(isReviewSection);
        const sourceBadges = Array.from(main.querySelectorAll('[data-and-source-rating], [data-and-source-app]')).filter(node => node.matches('[data-and-source-rating]') || /ryviu|r--widget|review/i.test(node.innerHTML));
        const incomingBadges = Array.from(incoming.querySelectorAll('[data-and-source-rating], [data-and-source-app]')).filter(node => node.matches('[data-and-source-rating]') || /ryviu|r--widget|review/i.test(node.innerHTML));
        incomingBadges.forEach(node => node.remove());
        const imported = Array.from(incoming.children).filter(node => !isReviewSection(node)).map(node => document.importNode(node,true));
        const importedControl = imported.flatMap(node => Array.from(node.querySelectorAll('and-inline-product-switch')))[0];
        const importedInfo = importedControl?.closest('product-info');
        if (!importedInfo) throw new Error('content');
        importedInfo.setAttribute('data-update-url','false');
        const badgeAnchor = importedInfo.querySelector('[id^="Price-"]')?.closest('.product__price') || importedInfo.querySelector('[id^="Price-"]') || importedControl;
        sourceBadges.forEach(node => {node.setAttribute('data-and-preserved-review','');badgeAnchor.before(node);});
        let reviewIndex = Array.from(incoming.children).findIndex(isReviewSection);
        if (reviewIndex < 0) reviewIndex = imported.length;
        reviewSections.forEach((node,index) => {node.setAttribute('data-and-preserved-review','');imported.splice(Math.min(reviewIndex+index,imported.length),0,node);});
        const position = window.scrollY;
        const oldNodes = Array.from(main.children);
        oldNodes.filter(node => !reviewSections.includes(node)).forEach(node => node.remove());
        imported.forEach(node => main.appendChild(node));
        window.andInlineProductReady = callback => callback(new Event('DOMContentLoaded'));
        for (const node of imported) {
          for (const script of node.querySelectorAll('script')) {
            if (script.closest('[data-and-preserved-review]') || /json/.test(script.type)) continue;
            if (script.src && loadedScripts.has(script.src)) continue;
            if (script.src) loadedScripts.add(script.src);
            const replacement = document.createElement('script');
            Array.from(script.attributes).forEach(attr => replacement.setAttribute(attr.name,attr.value));
            replacement.textContent = script.textContent.replace(/document\.addEventListener\(\s*['"]DOMContentLoaded['"]\s*,/g,'window.andInlineProductReady(');
            script.replaceWith(replacement);
          }
          node.dispatchEvent(new CustomEvent('shopify:section:load',{bubbles:true,detail:{sectionId:node.id.replace('shopify-section-','')}}));
        }
        window.scrollTo({top:position,behavior:'instant'});
      } catch {
        error.textContent = '暂时无法加载该配置，请确认两款整车都可在当前市场访问，并使用带有此独立区块的产品模板。请稍后重试。';
        error.hidden = false;
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
      }
    }
    sync(value) {
      this.querySelector('.form__label strong').textContent = value;
      this.querySelectorAll('a[data-product-handle]').forEach(link => {
        const active = link.dataset.productHandle === this.dataset.currentProduct && link.dataset.optionValue === value;
        link.classList.toggle('is-selected',active);
        if (active) link.setAttribute('aria-current','page'); else link.removeAttribute('aria-current');
      });
    }
    disconnectedCallback() {
      this.unsubscribe?.();
      if(this.onClick)this.removeEventListener('click',this.onClick,true);
      this.onClick=null;
    }
  }
  customElements.define('and-inline-product-switch',InlineProductSwitch);
})();
